'use client';

import { ReactPlugin } from '@microsoft/applicationinsights-react-js';
import { ApplicationInsights, ITelemetryItem } from '@microsoft/applicationinsights-web';

import {
  enrichOpaqueScriptErrorTelemetry,
  isOpaqueCrossOriginScriptError,
} from './opaque-script-error';
import { registerRuntimeErrorCapture } from './runtime-error-capture';
import {
  consumeScriptErrorContext,
  registerScriptErrorContextListener,
} from './script-error-context';

let reactPlugin: ReactPlugin | null = null;
let appInsights: ApplicationInsights | null = null;

const defaultBrowserHistory = {
    url: "/",
    location: { pathname: "" },
    listen: () => {}, // eslint-disable-line
};

let browserHistory = defaultBrowserHistory;
if (typeof window !== "undefined") {
    browserHistory = { ...browserHistory, ...window.history };
    browserHistory.location.pathname = browserHistory?.url;
}

export const initApplicationInsights = () => {
  if (typeof window !== 'undefined' && !appInsights) {
    const connectionString = process.env.NEXT_PUBLIC_APPLICATIONINSIGHTS_CONNECTION_STRING;
    const roleName = process.env.NEXT_PUBLIC_APPLICATIONINSIGHTS_ROLE_NAME;

    // Skip initialization when no connection string is configured (e.g. local dev)
    // to avoid the SDK throwing "Please provide instrumentation key".
    if (!connectionString) {
      console.warn('Application Insights connection string not found');
      return { reactPlugin, appInsights };
    }

    reactPlugin = new ReactPlugin();

    appInsights = new ApplicationInsights({
      config: {
        connectionString: connectionString,
        extensions: [reactPlugin],
        extensionConfig: {
            [reactPlugin.identifier]: { history: browserHistory },
        },
        enableAutoRouteTracking: false
      }
    });

    registerScriptErrorContextListener();

    appInsights.addTelemetryInitializer((env: ITelemetryItem) => {
      env.tags = env.tags || {};
      env.tags["ai.cloud.role"] = roleName;

      const pathname = window.location.pathname;

      if (env.baseType === 'PageviewData' && env.baseData) {
        env.tags["ai.operation.name"] = pathname;
      }

      if (env.baseType === 'ExceptionData' && env.baseData) {
        env.tags["ai.operation.name"] = pathname;

        if (isOpaqueCrossOriginScriptError(env.baseData)) {
          const scriptContext = consumeScriptErrorContext();
          enrichOpaqueScriptErrorTelemetry(env.baseData, scriptContext?.scriptSrc);
        }
      }
    });

    appInsights.loadAppInsights();

    registerRuntimeErrorCapture(appInsights);
  }

  return { reactPlugin, appInsights };
};

export const getApplicationInsights = () => {
    return { reactPlugin, appInsights };
};

export const buildFullUrl = (pathname: string): string | null => {
    if (typeof window === 'undefined') {
        return null;
    }

    // Include base path if configured (e.g., /woningcheck for apps behind Front Door)
    const basePath = process.env.NEXT_PUBLIC_BASE_PATH || '';
    const fullPath = basePath ? `${basePath}${pathname}` : pathname;

    return `${window.location.origin}${fullPath}`;
};
